/**
 * Stores API wrapper. Mirrors `apps/api/src/modules/stores` routes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";

import type { APIError } from "../http.js";
import { qk } from "../query-keys.js";

import { apiRequest } from "./_client.js";

export interface StoreInfo {
  id: string;
  code: string;
  name: string;
  storeType: string | null;
  region: string | null;
  city: string | null;
  stateRegion: string | null;
  postalCode: string | null;
  countryCode: string | null;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
  isActive: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  createdByEmail: string | null;
  updatedByName: string | null;
  updatedByEmail: string | null;
}

export interface StoresListResponse {
  items: StoreInfo[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ListStoresQuery {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  search?: string;
}

export interface CreateStoreRequest {
  code: string;
  name: string;
  storeType?: string | null;
  region?: string | null;
  city?: string | null;
  stateRegion?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  timezone?: string;
  latitude?: number | null;
  longitude?: number | null;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
}

export type UpdateStoreRequest = Partial<CreateStoreRequest>;

export interface StoreCategoryLink {
  id: string;
  categoryId: string;
  categoryCode: string;
  categoryName: string;
  isActive: boolean;
  sortOrder: number | null;
}

export interface StoreCategoriesListResponse {
  items: StoreCategoryLink[];
}

export interface LinkStoreCategoryRequest {
  categoryId: string;
  isActive?: boolean;
  sortOrder?: number | null;
}

export interface UpdateStoreCategoryRequest {
  isActive?: boolean;
  sortOrder?: number | null;
}

export interface BulkSetStoreCategoriesResponse {
  affected: number;
}

export const storesApi = {
  list: (query: ListStoresQuery = {}, signal?: AbortSignal): Promise<StoresListResponse> =>
    apiRequest({
      method: "GET",
      url: "/api/v2/stores",
      params: query as Record<string, string | number | boolean | null | undefined>,
      signal,
    }),

  get: (id: string, signal?: AbortSignal): Promise<StoreInfo> =>
    apiRequest({ method: "GET", url: `/api/v2/stores/${encodeURIComponent(id)}`, signal }),

  create: (body: CreateStoreRequest): Promise<StoreInfo> =>
    apiRequest({ method: "POST", url: "/api/v2/stores", data: body }),

  update: (id: string, patch: UpdateStoreRequest): Promise<StoreInfo> =>
    apiRequest({
      method: "PATCH",
      url: `/api/v2/stores/${encodeURIComponent(id)}`,
      data: patch,
    }),

  listCategories: (
    storeId: string,
    params: { activeOnly?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<StoreCategoriesListResponse> =>
    apiRequest({
      method: "GET",
      url: `/api/v2/stores/${encodeURIComponent(storeId)}/categories`,
      params: params as Record<string, string | number | boolean | null | undefined>,
      signal,
    }),

  linkCategory: (storeId: string, body: LinkStoreCategoryRequest): Promise<StoreCategoryLink> =>
    apiRequest({
      method: "POST",
      url: `/api/v2/stores/${encodeURIComponent(storeId)}/categories`,
      data: body,
    }),

  updateCategoryLink: (
    storeId: string,
    categoryId: string,
    patch: UpdateStoreCategoryRequest,
  ): Promise<StoreCategoryLink> =>
    apiRequest({
      method: "PATCH",
      url: `/api/v2/stores/${encodeURIComponent(storeId)}/categories/${encodeURIComponent(categoryId)}`,
      data: patch,
    }),

  bulkSetCategories: (
    storeId: string,
    isActive: boolean,
  ): Promise<BulkSetStoreCategoriesResponse> =>
    apiRequest({
      method: "POST",
      url: `/api/v2/stores/${encodeURIComponent(storeId)}/categories/bulk`,
      data: { isActive },
    }),
};

export function useStoresQuery(
  query: ListStoresQuery = {},
  options?: Omit<UseQueryOptions<StoresListResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<StoresListResponse, APIError>({
    queryKey: qk.stores.list(query as Record<string, unknown>),
    queryFn: ({ signal }) => storesApi.list(query, signal),
    retry: false,
    staleTime: 30_000,
    ...options,
  });
}

export function useStoreQuery(
  id: string,
  options?: Omit<UseQueryOptions<StoreInfo, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<StoreInfo, APIError>({
    queryKey: qk.stores.detail(id),
    queryFn: ({ signal }) => storesApi.get(id, signal),
    retry: false,
    enabled: id !== "",
    ...options,
  });
}

export function useCreateStoreMutation(
  options?: Omit<UseMutationOptions<StoreInfo, APIError, CreateStoreRequest>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<StoreInfo, APIError, CreateStoreRequest>({
    ...options,
    mutationFn: (body) => storesApi.create(body),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.stores.all });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUpdateStoreMutation(
  options?: Omit<
    UseMutationOptions<StoreInfo, APIError, { id: string; patch: UpdateStoreRequest }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<StoreInfo, APIError, { id: string; patch: UpdateStoreRequest }>({
    ...options,
    mutationFn: ({ id, patch }) => storesApi.update(id, patch),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.stores.all });
      void qc.invalidateQueries({ queryKey: qk.stores.detail(vars.id) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useStoreCategoriesQuery(
  storeId: string,
  options?: Omit<UseQueryOptions<StoreCategoriesListResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<StoreCategoriesListResponse, APIError>({
    queryKey: qk.stores.categories(storeId),
    queryFn: ({ signal }) => storesApi.listCategories(storeId, {}, signal),
    retry: false,
    enabled: storeId !== "",
    ...options,
  });
}

/** Only categories linked AND active in the store — used to populate New Scan. */
export function useStoreActiveCategoriesQuery(
  storeId: string,
  options?: Omit<UseQueryOptions<StoreCategoriesListResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<StoreCategoriesListResponse, APIError>({
    queryKey: qk.stores.activeCategories(storeId),
    queryFn: ({ signal }) => storesApi.listCategories(storeId, { activeOnly: true }, signal),
    retry: false,
    enabled: storeId !== "",
    ...options,
  });
}

export function useLinkStoreCategoryMutation(
  options?: Omit<
    UseMutationOptions<
      StoreCategoryLink,
      APIError,
      { storeId: string; body: LinkStoreCategoryRequest }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    StoreCategoryLink,
    APIError,
    { storeId: string; body: LinkStoreCategoryRequest }
  >({
    ...options,
    mutationFn: ({ storeId, body }) => storesApi.linkCategory(storeId, body),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.stores.categories(vars.storeId) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUpdateStoreCategoryMutation(
  options?: Omit<
    UseMutationOptions<
      StoreCategoryLink,
      APIError,
      { storeId: string; categoryId: string; patch: UpdateStoreCategoryRequest }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    StoreCategoryLink,
    APIError,
    { storeId: string; categoryId: string; patch: UpdateStoreCategoryRequest }
  >({
    ...options,
    mutationFn: ({ storeId, categoryId, patch }) =>
      storesApi.updateCategoryLink(storeId, categoryId, patch),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.stores.categories(vars.storeId) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useBulkSetStoreCategoriesMutation(
  options?: Omit<
    UseMutationOptions<
      BulkSetStoreCategoriesResponse,
      APIError,
      { storeId: string; isActive: boolean }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    BulkSetStoreCategoriesResponse,
    APIError,
    { storeId: string; isActive: boolean }
  >({
    ...options,
    mutationFn: ({ storeId, isActive }) => storesApi.bulkSetCategories(storeId, isActive),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.stores.categories(vars.storeId) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}
